import { Box, Grid, MenuItem } from "@mui/material";
import { Form, Formik } from "formik";
import { toast } from "react-toastify";
import * as Yup from "yup";
import getAxiosError from "../../../app/api/axiosError";
import { addParcelCenter } from "../../../app/features/parcelCenter/parcelCenterSlice";
import { RootState, useAppDispatch } from "../../../app/features/store/store";
import { AddParcelCenterRequest } from "../../../app/models/requestModels/parcelCenterRequest";
import FormikTextField from "../../../components/common/formikFields/FormikTextField";
import SubmitModalWithFormik from "../../../components/common/modalPopup/SubmitModalWithFormik";
import {
  ADDRESS_REGEX,
  ADDRESS_REGEX_INVALID_MSG,
  AIRPORT_CODE_REGEX,
  AIRPORT_CODE_REGEX_INVALID_MSG,
  CITY_REGEX,
  CITY_REGEX_INVALID_MSG,
  ZIPCODE_REGEX,
  ZIPCODE_REGEX_INVALID_MSG,
} from "../../../helpers/AppConstants";
import { STATES_LIST } from "../../fdp/FdpConstants";
import { useSelector } from "react-redux";

interface AddParcelCenterModalProps {
  closeAddParcelCenterModal: CallableFunction;
  onCompleted: CallableFunction;
}

const AddParcelCenterModal = ({
  closeAddParcelCenterModal,
  onCompleted,
}: AddParcelCenterModalProps) => {
  const { authResponse } = useSelector((state: RootState) => state.login);

  const dispatch = useAppDispatch();

  const validationSchema = Yup.object().shape({
    parcelCenterName: Yup.string()
      .required("Parcel Center Nearest AirPort code is required")
      .matches(AIRPORT_CODE_REGEX, AIRPORT_CODE_REGEX_INVALID_MSG),
    address: Yup.string()
      .required("Address is required")
      .matches(ADDRESS_REGEX, ADDRESS_REGEX_INVALID_MSG),
    city: Yup.string()
      .required("City is required")
      .matches(CITY_REGEX, CITY_REGEX_INVALID_MSG),
    state: Yup.string().required("State is required"),
    zipCode: Yup.string()
      .required("Zip Code is required")
      .matches(ZIPCODE_REGEX, ZIPCODE_REGEX_INVALID_MSG),
    timezone: Yup.string().required("Timezone is required"),
  });

  const initialValues: AddParcelCenterRequest = {
    parcelCenterName: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    timezone: "",
    createdBy: authResponse?.userSettings?.userName ?? "",
    updatedBy: authResponse?.userSettings?.userName ?? "",
  };

  const handleSubmit = async (values: AddParcelCenterRequest) => {
    try {
      const response = await dispatch(addParcelCenter(values)).unwrap();
      if (response.isSuccess) {
        toast.success(response.message!, {
          toastId: "addParcelCenterSuccess",
        });
        onCompleted();
      } else {
        toast.error(response.message!, {
          toastId: "addParcelCenterError",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "addParcelCenterException",
      });
    }
    return true;
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values, { setSubmitting }) => {
        setSubmitting(true);
        handleSubmit(values).finally(() => setSubmitting(false));
      }}
    >
      {({ isSubmitting, values }) => (
        <SubmitModalWithFormik
          title="Add Parcel Center"
          closeSubmitModal={closeAddParcelCenterModal}
          allowSubmit={true}
          size="lg"
          isLoading={isSubmitting}
          submitButtonName="Add"
          closeModalAfterSubmit={true}
        >
          <Form>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Grid container rowSpacing={2} columnSpacing={2}>
                <Grid item xs={12} sm={12} md={12} lg={12}>
                  <FormikTextField
                    margin="normal"
                    id="parcelCenterName"
                    label="Parcel Center Nearest Air-Port Code"
                    name="parcelCenterName"
                    size="small"
                    fullWidth
                    required
                    variant={"outlined"}
                  />
                </Grid>
              </Grid>
              <Grid container rowSpacing={2} columnSpacing={2}>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <FormikTextField
                    margin="normal"
                    id="address"
                    label="Address"
                    name="address"
                    size="small"
                    fullWidth
                    required
                    variant={"outlined"}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <FormikTextField
                    margin="normal"
                    id="city"
                    label="City"
                    name="city"
                    size="small"
                    fullWidth
                    required
                    variant={"outlined"}
                  />
                </Grid>
              </Grid>
              <Grid container rowSpacing={2} columnSpacing={2}>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <FormikTextField
                    margin="normal"
                    id="state"
                    label="State"
                    name="state"
                    size="small"
                    fullWidth
                    required
                    select
                    variant={"outlined"}
                    SelectProps={{
                      MenuProps: { PaperProps: { style: { maxHeight: 150 } } },
                    }}
                  >
                    {STATES_LIST.map((state, index) => (
                      <MenuItem key={index} value={state}>
                        {state}
                      </MenuItem>
                    ))}
                  </FormikTextField>
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <FormikTextField
                    margin="normal"
                    id="zipCode"
                    label="Zip Code"
                    name="zipCode"
                    size="small"
                    fullWidth
                    required
                    variant={"outlined"}
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={12} lg={12}>
                  <FormikTextField
                    margin="normal"
                    id="timezone"
                    label="Timezone"
                    name="timezone"
                    size="small"
                    fullWidth
                    required
                    variant={"outlined"}
                  />
                </Grid>
              </Grid>
            </Box>
          </Form>
        </SubmitModalWithFormik>
      )}
    </Formik>
  );
};

export default AddParcelCenterModal;
